import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ArrowsRightLeftIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Select from '@/components/ui/Select';
import LoadingOverlay, { WidgetWrapper } from '@/components/ui/LoadingOverlay';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  TableEmpty,
} from '@/components/ui/Table';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { apiClient } from '@/lib/api';
import type { TelemetryRun } from '@/types/api';
import { formatDuration } from '@/types/api';

// Mock step timings - NOT CONNECTED yet
const stepTimings = [
  { step: 'PDF Ingest', avgTime: 2 },
  { step: 'Image Process', avgTime: 15 },
  { step: 'Chapter Detect', avgTime: 3 },
  { step: 'TTS Synth', avgTime: 35 },
  { step: 'Audio Assemble', avgTime: 5 },
  { step: 'Credits Gen', avgTime: 1 },
  { step: 'QA Finalize', avgTime: 2 },
];

// Mock performance data - NOT CONNECTED yet
const performanceData = [
  { date: 'Jan 1', duration: 42, success: 98 },
  { date: 'Jan 8', duration: 45, success: 97 },
  { date: 'Jan 15', duration: 38, success: 99 },
  { date: 'Jan 22', duration: 52, success: 96 },
  { date: 'Jan 29', duration: 44, success: 98 },
  { date: 'Feb 5', duration: 41, success: 99 },
];

function getStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <Badge variant="success">Success</Badge>;
    case 'running':
    case 'processing':
      return <Badge variant="primary">Running</Badge>;
    case 'failed':
      return <Badge variant="danger">Failed</Badge>;
    default:
      return <Badge variant="slate">{status}</Badge>;
  }
}

export default function TTSTelemetryPage() {
  const [dateRange, setDateRange] = useState('7d');

  // Real data state
  const [runs, setRuns] = useState<TelemetryRun[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch telemetry runs from backend
  const fetchRuns = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);
      else setIsRefreshing(true);

      const response = await apiClient.get<TelemetryRun[]>('tts/telemetry');
      setRuns(response);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch telemetry runs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch telemetry');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  // Poll for updates every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchRuns(false);
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchRuns]);

  // Calculate stats from real data
  const totalRuns = runs.length;
  const successRate =
    (runs.filter(r => r.status === 'completed').length / Math.max(1, runs.length) * 100);
  const avgDurationMs = runs.reduce((sum, r) => sum + (r.duration_ms || 0), 0) / Math.max(1, runs.length);
  const avgDuration = formatDuration(avgDurationMs);
  const activeRuns = runs.filter(r => r.status === 'processing').length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Pipeline Telemetry</h1>
          <p className="text-slate-500 mt-1">Monitor performance and analyze pipeline runs</p>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />}
            onClick={() => fetchRuns(false)}
            disabled={isRefreshing}
          >
            Refresh
          </Button>
          <Link to="/tts/telemetry/compare">
            <Button variant="outline" leftIcon={<ArrowsRightLeftIcon className="w-4 h-4" />}>
              Compare Runs
            </Button>
          </Link>
          <Button variant="outline" leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}>
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards - Connected to Real Data */}
      <LoadingOverlay isLoading={isLoading} loadingOpacity={40} showSpinner={false}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardBody className="text-center">
              <p className="text-3xl font-bold text-slate-800">{totalRuns.toLocaleString()}</p>
              <p className="text-sm text-slate-500 mt-1">Total Runs</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="text-center">
              <p className="text-3xl font-bold text-slate-800">{avgDuration}</p>
              <p className="text-sm text-slate-500 mt-1">Avg Duration</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="text-center">
              <p className="text-3xl font-bold text-success-500">{successRate.toFixed(1)}%</p>
              <p className="text-sm text-slate-500 mt-1">Success Rate</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="text-center">
              <p className="text-3xl font-bold text-primary-500">{activeRuns}</p>
              <p className="text-sm text-slate-500 mt-1">Active Runs</p>
            </CardBody>
          </Card>
        </div>
      </LoadingOverlay>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Over Time - NOT CONNECTED (20% opacity) */}
        <WidgetWrapper isConnected={false}>
          <Card>
            <CardHeader
              title="Performance Over Time"
              action={
                <Select
                  options={[
                    { value: '7d', label: 'Last 7 days' },
                    { value: '30d', label: 'Last 30 days' },
                    { value: '90d', label: 'Last 90 days' },
                  ]}
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="w-36"
                />
              }
            />
            <CardBody>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={performanceData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="date" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px',
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="duration"
                      stroke="#4AC3C3"
                      strokeWidth={2}
                      dot={{ fill: '#4AC3C3' }}
                      name="Duration (min)"
                    />
                    <Line
                      type="monotone"
                      dataKey="success"
                      stroke="#4CAF50"
                      strokeWidth={2}
                      dot={{ fill: '#4CAF50' }}
                      name="Success %"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardBody>
          </Card>
        </WidgetWrapper>

        {/* Step Timings - NOT CONNECTED (20% opacity) */}
        <WidgetWrapper isConnected={false}>
          <Card>
            <CardHeader title="Average Step Timings" subtitle="Minutes per pipeline step" />
            <CardBody>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stepTimings} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" stroke="#94A3B8" fontSize={12} />
                    <YAxis
                      type="category"
                      dataKey="step"
                      stroke="#94A3B8"
                      fontSize={11}
                      width={100}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px',
                      }}
                    />
                    <Bar dataKey="avgTime" fill="#4AC3C3" radius={[0, 4, 4, 0]} name="Avg Time (min)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardBody>
          </Card>
        </WidgetWrapper>
      </div>

      {/* Recent Runs Table - Connected to Real Data */}
      <LoadingOverlay isLoading={isLoading} loadingOpacity={40}>
        <Card>
          <CardHeader title="Recent Runs" subtitle="Latest pipeline executions" />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow isHoverable={false}>
                  <TableHeaderCell>Run ID</TableHeaderCell>
                  <TableHeaderCell>Job</TableHeaderCell>
                  <TableHeaderCell>Duration</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Started</TableHeaderCell>
                  <TableHeaderCell>Completed</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {error ? (
                  <TableEmpty
                    message="Failed to load telemetry"
                    description={error}
                    colSpan={7}
                  />
                ) : runs.length === 0 && !isLoading ? (
                  <TableEmpty
                    message="No telemetry runs found"
                    description="Pipeline runs will appear here once jobs are processed"
                    colSpan={7}
                  />
                ) : (
                  runs.slice(0, 20).map((run) => (
                    <TableRow key={run.id}>
                      <TableCell className="font-mono text-sm">{run.run_id}</TableCell>
                      <TableCell>
                        <p className="font-medium text-slate-800">{run.run_id}</p>
                        <p className="text-xs text-slate-400">{run.id}</p>
                      </TableCell>
                      <TableCell>{run.duration_ms !== null ? formatDuration(run.duration_ms) : '--'}</TableCell>
                      <TableCell>{getStatusBadge(run.status)}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {run.started_at ? new Date(run.started_at).toLocaleString() : '--'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {run.completed_at ? new Date(run.completed_at).toLocaleString() : '--'}
                      </TableCell>
                      <TableCell>
                        <Link to={`/tts/telemetry/${run.run_id}`}>
                          <Button variant="ghost" size="sm" leftIcon={<EyeIcon className="w-4 h-4" />}>
                            View
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </LoadingOverlay>

      {/* GPT Concurrency Analysis - NOT CONNECTED */}
      <WidgetWrapper isConnected={false}>
        <Card>
          <CardHeader
            title="GPT Concurrency Analysis"
            subtitle="View API call patterns and optimize throughput"
            action={
              runs.length > 0 ? (
                <Link to={`/tts/telemetry/${runs[0]?.run_id}`}>
                  <Button variant="outline" size="sm" leftIcon={<ChartBarIcon className="w-4 h-4" />}>
                    Analyze Latest
                  </Button>
                </Link>
              ) : null
            }
          />
          <CardBody>
            <p className="text-red-500 opacity-50">
              GPT Concurrency Analysis is not yet connected to the backend.
              Select a run from the table above to view detailed pipeline analysis.
            </p>
          </CardBody>
        </Card>
      </WidgetWrapper>
    </div>
  );
}
